/* eslint-disable class-methods-use-this */
import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import {
    Box, Flex, Grid, Icon, Link, Text
} from 'components/ui';
import mediaUtils from 'utils/Media';
import ItemSubstitutionRoot from 'components/ItemSubstitution/ItemSubstitutionRoot';
import ProductImage from 'components/Product/ProductImage/ProductImage';
import OnlyFewLeft from 'components/RwdBasket/Carts/CartLayout/SkuItem/OnlyFewLeft';
import OutOfStockFlag from 'components/RwdBasket/Carts/CartLayout/SkuItem/OutOfStockFlag';
import FinalSaleMessage from 'components/RwdBasket/Carts/CartLayout/SkuItem/FinalSaleMessage';
import ButtonRow from 'components/RwdBasket/Carts/CartLayout/SkuItem/Buttons/ButtonRow';
import { colors, radii, space } from 'style/config';
import rwdBasketUtils from 'utils/RwdBasket';
import { getImageAltText } from 'utils/Accessibility';
import Location from 'utils/Location';
import localeUtils from 'utils/LanguageLocale';
import helperUtils from 'utils/Helpers';
import CartLayoutEN from 'components/RwdBasket/Carts/CartLayout/locales/CartLayout_en_US';
import CartLayoutFR from 'components/RwdBasket/Carts/CartLayout/locales/CartLayout_fr_CA';
import OOSAlternateLink from 'components/RwdBasket/Carts/CartLayout/SkuItem/OOSAlternateLink/OOSAlternateLink';
import RwdBasketActions from 'actions/RwdBasketActions/RwdBasketActions';
import Empty from 'constants/empty';
import skuUtils from 'utils/Sku';
import OOSAlternateLinkBindings from 'analytics/bindingMethods/components/rwdBasket/OOSAlternateLinkBindings';
import BasketConstant from 'constants/Basket';

const { openFreeSamplesModal, openBiRewardsModal } = RwdBasketActions;
const { Media } = mediaUtils;
const { getTargetUrl } = rwdBasketUtils;
const getText = localeUtils.getLocalisedDataFromResource(CartLayoutEN, CartLayoutFR);
const { formatSiteCatalystPrice, deferTaskExecution } = helperUtils;

const styles = {
    salePriceText: {
        color: colors.red
    },
    originalPriceText: {
        fontWeight: 'var(--font-weight-normal)',
        textDecoration: 'line-through'
    },
    priceFlex: {
        fontWeight: 'var(--font-weight-bold)'
    },
    savingsBadge: {
        backgroundColor: colors.red,
        color: colors.white,
        borderRadius: radii[3],
        paddingLeft: space[2],
        paddingRight: space[2],
        fontSize: 'var(--font-size-sm)',
        display: 'inline-flex',
        alignItems: 'center',
        textTransform: 'uppercase'
    },
    skuDeleteWrapper: {
        justifyContent: 'center',
        alignItems: 'center',
        width: space[5],
        height: space[5],
        borderRadius: radii.full,
        border: `1px solid ${colors.midGray}`,
        cursor: 'pointer'
    },
    iconBox: {
        width: space[3],
        height: space[3]
    }
};

function TitleLink({ brandName, productName, targetUrl, goToTargetUrl }) {
    const displayName = (
        <>
            <strong data-at={Sephora.debug.dataAt('bsk_sku_brand')}>{brandName}</strong>
            <br />
            <span data-at={Sephora.debug.dataAt('bsk_sku_name')}>{productName}</span>
        </>
    );

    return (
        <Text
            is='p'
            fontSize={['sm', 'base']}
            marginBottom={1}
        >
            {targetUrl ? (
                <Link
                    display='block'
                    onClick={goToTargetUrl}
                    href={targetUrl}
                >
                    {displayName}
                </Link>
            ) : (
                displayName
            )}
        </Text>
    );
}

function Price({ item, price, addItemSavingsBasketExperience = Empty.Object }) {
    const hasSalePrice = item.sku.salePrice || (item.isReplenishment && (item.replenishmentItemPrice || item.sku.replenishmentAdjusterPrice));
    const salePrice = item.isReplenishment ? item.replenishmentItemPrice || item.sku.replenishmentAdjusterPrice : item.salePriceSubtotal;
    const originalPrice = price || item.listPriceSubtotal;
    const isCanada = localeUtils.isCanada();
    const { challengerOne, challengerTwo } = addItemSavingsBasketExperience;
    const defaultPrice = (
        <Flex
            css={styles.priceFlex}
            fontSize={['base', 'md']}
            marginTop={3}
            gap={1}
        >
            {hasSalePrice && (
                <div
                    data-at={Sephora.debug.dataAt('bsk_sale_price')}
                    css={styles.salePriceText}
                    children={salePrice + (item.sku.acceleratedPromotion ? '*' : '')}
                />
            )}
            <div
                data-at={Sephora.debug.dataAt('bsk_sku_price')}
                css={hasSalePrice ? styles.originalPriceText : null}
                children={originalPrice}
            />
        </Flex>
    );

    const savingsDetailsAbTest = (challengerOne || challengerTwo) && hasSalePrice && (
        <BasketSavings
            item={item}
            price={price}
            addItemSavingsBasketExperience={addItemSavingsBasketExperience}
        />
    );

    const savingsDetails = hasSalePrice && isCanada && (
        <BasketSavings
            item={item}
            price={price}
        />
    );

    return savingsDetailsAbTest || savingsDetails || defaultPrice;
}

function BasketSavings({ item, price, addItemSavingsBasketExperience = {} }) {
    const hasSalePrice = item.sku.salePrice || (item.isReplenishment && (item.replenishmentItemPrice || item.sku.replenishmentAdjusterPrice));

    const salePrice = item.isReplenishment ? item.replenishmentItemPrice || item.sku.replenishmentAdjusterPrice : item.salePriceSubtotal;

    const originalPrice = price || item.listPriceSubtotal;

    const calculateSavings = () => {
        if (!hasSalePrice) {
            return null;
        }

        const originalAmount = parseFloat(formatSiteCatalystPrice(originalPrice));
        const saleAmount = parseFloat(formatSiteCatalystPrice(salePrice));
        const savings = originalAmount - saleAmount;

        return savings.toFixed(2);
    };

    const formatSavingsAmount = () => {
        const savingsAmount = calculateSavings();

        if (!savingsAmount) {
            return null;
        }

        const cents = savingsAmount.split('.')[1];

        if (cents === '00') {
            return savingsAmount.split('.')[0];
        }

        return savingsAmount;
    };

    const { challengerOne, challengerTwo } = addItemSavingsBasketExperience;

    if (challengerOne) {
        return (
            <Box>
                <Flex
                    css={styles.priceFlex}
                    fontSize={['base', 'md']}
                    marginTop={3}
                    gap={1}
                >
                    <div
                        data-at={Sephora.debug.dataAt('bsk_sale_price')}
                        css={styles.salePriceText}
                        children={salePrice + (item.sku.acceleratedPromotion ? '*' : '')}
                    />
                    <div
                        data-at={Sephora.debug.dataAt('bsk_sku_price')}
                        css={styles.originalPriceText}
                        children={originalPrice}
                    />
                    <Box
                        css={styles.savingsBadge}
                        data-at={Sephora.debug.dataAt('bsk_sku_savings')}
                    >
                        {getText('save')} ${formatSavingsAmount()}
                    </Box>
                </Flex>
            </Box>
        );
    }

    if (challengerTwo) {
        return (
            <Box>
                <Flex
                    css={styles.priceFlex}
                    fontSize={['base', 'md']}
                    marginTop={3}
                    gap={1}
                >
                    <div
                        data-at={Sephora.debug.dataAt('bsk_sale_price')}
                        css={styles.salePriceText}
                        children={salePrice + (item.sku.acceleratedPromotion ? '*' : '')}
                    />
                    <div
                        data-at={Sephora.debug.dataAt('bsk_sku_price')}
                        css={styles.originalPriceText}
                        children={originalPrice}
                    />
                    <Text
                        fontWeight='var(--font-weight-bold)'
                        data-at={Sephora.debug.dataAt('bsk_sku_savings')}
                        color={colors.red}
                    >
                        {getText('save')} ${formatSavingsAmount()}
                    </Text>
                </Flex>
            </Box>
        );
    }

    return (
        <Box>
            <Flex
                css={styles.priceFlex}
                fontSize={['base', 'md']}
                marginTop={3}
                gap={1}
            >
                <div
                    data-at={Sephora.debug.dataAt('bsk_sale_price')}
                    css={styles.salePriceText}
                    children={salePrice + (item.sku.acceleratedPromotion ? '*' : '')}
                />
                <div
                    data-at={Sephora.debug.dataAt('bsk_sku_price')}
                    css={styles.originalPriceText}
                    children={originalPrice}
                />
            </Flex>
            <Text
                fontSize='sm'
                marginTop={1}
                fontWeight='var(--font-weight-bold)'
                data-at={Sephora.debug.dataAt('bsk_sku_savings')}
                color={colors.gray}
            >
                {getText('save')} ${calculateSavings()}
            </Text>
        </Box>
    );
}

function ErrorContainer({ error }) {
    if (!error.isAvailable) {
        return null;
    }

    const { message, ref } = error;

    return (
        <Box ref={ref}>
            <Box
                fontSize={['sm', 'base']}
                color={colors.red}
                top={'-8px'}
                bottom={2}
                position={'relative'}
            >
                {message}
            </Box>
        </Box>
    );
}

function SkuItemLayout({
    item,
    itemDeliveryMethod,
    abovePriceContent,
    belowPriceContent = [],
    price = null,
    rightLGUISection,
    isNotFinalSale,
    isOutOfStock = false,
    isDisabled,
    onDelete,
    addItemSavingsBasketExperience,
    ...props
}) {
    const targetUrl = getTargetUrl(item);

    // Main Kill Switch used to hide/show the OOS Alternate Link feature
    const showOOSAlternateLink = Sephora.configurationSettings?.isSelectAlternateForSamples?.isEnabled || false;
    let isAllowedOOS = true;

    if (skuUtils.isBirthdayGift(item.sku)) {
        isAllowedOOS = false;
    }

    if (skuUtils.isRougeRewardCard(item.sku)) {
        isAllowedOOS = false;
    }

    if (skuUtils.isBiReward(item.sku) && skuUtils.getBiPoints(item.sku) >= BasketConstant.STANDALONE_REWARD_MIN_VAL) {
        isAllowedOOS = false;
    }

    const goToTargetUrl = event => {
        event.preventDefault();

        deferTaskExecution(() => {
            Location.navigateTo(event, targetUrl);
        });
    };

    const renderButtonRow = breakpoint => (
        <ButtonRow
            item={item}
            {...props}
            isOutOfStock={isOutOfStock}
            itemDeliveryMethod={itemDeliveryMethod}
            breakpoint={breakpoint}
            isDisabled={isDisabled}
            onDelete={onDelete}
        />
    );

    const handleOOSAlternativeClick = () => {
        const isSample = skuUtils.isSample(item.sku);
        const isBiReward = skuUtils.isBiReward(item.sku);
        const isPDPSample = skuUtils.isPDPSample(item.sku);
        const isGwpSample = skuUtils.isGwp(item.sku);
        const isBirthdayReward = skuUtils.isBirthdayGift(item.sku);
        const isBiRewardOver750 = skuUtils.isBiRewardOver750Points(item.sku);

        // Do not allow select alternate for Birthday Rewards, BI Rewards over 750 points, or GWP items
        if (isBirthdayReward || isBiRewardOver750 || isGwpSample) {
            return;
        }

        OOSAlternateLinkBindings.trackSelectAlternateClick(item);

        if (isSample) {
            openFreeSamplesModal(true, {
                replaceProduct: true,
                skuToReplace: item?.sku || Empty?.object
            });
        }

        if (isBiReward) {
            openBiRewardsModal(true, {
                replaceProduct: true,
                skuToReplace: item?.sku || Empty?.object
            });
        }

        if (isPDPSample) {
            // TODO: openBiRewardsModal(true, argumentsObj);
        }
    };

    if (item.sku.isOnlyFewLeft) {
        belowPriceContent.push(OnlyFewLeft);
    }

    const OOSLink = () =>
        showOOSAlternateLink && isAllowedOOS ? (
            <OOSAlternateLink
                item={item}
                isOutOfStock={true}
                clickHandler={handleOOSAlternativeClick}
            />
        ) : (
            <></>
        );

    if (isOutOfStock) {
        belowPriceContent.push(() => {
            return (
                <Flex
                    alignItems='center'
                    gap={2}
                >
                    <OutOfStockFlag itemDeliveryMethod={itemDeliveryMethod} />
                    <OOSLink />
                </Flex>
            );
        });
    }

    if (!isNotFinalSale && !item.sku.isReturnable) {
        belowPriceContent.push(FinalSaleMessage);
    }

    return (
        <Grid
            columns={[null, '1fr auto']}
            gap={0}
            marginLeft={[3, 5]}
            marginRight={[2, 4]}
            borderBottom={`1px solid ${colors.lightGray}`}
            paddingY={4}
            data-at={Sephora.debug.dataAt('product_refinement')}
        >
            <Flex
                flexDirection={'column'}
                gap={0}
                marginRight={[null, null, null, 4]}
            >
                <ErrorContainer error={item.error} />
                <Grid
                    gap={[`${space[4]}px`, 4]}
                    columns='auto 1fr'
                    lineHeight='tight'
                >
                    <Box
                        onClick={goToTargetUrl}
                        href={targetUrl}
                        margin={[2, 1]}
                        data-at={Sephora.debug.dataAt('product_image_link')}
                    >
                        <Box
                            is='div'
                            display={'inline'}
                            position={'relative'}
                        >
                            {(isOutOfStock || item.error.isAvailable) && (
                                <Box
                                    position={'relative'}
                                    zIndex={1}
                                    style={{ padding: '10px' }}
                                >
                                    <Icon
                                        name={'alert'}
                                        color={'red'}
                                        style={{ position: 'absolute', top: '50%', transform: 'translate(-75%, -50%)' }}
                                    />
                                </Box>
                            )}
                            <ProductImage
                                id={item.sku.skuId}
                                size={[74, 190]}
                                skuImages={item.sku.skuImages}
                                isPageRenderImg={item.enablePageRenderTracking}
                                disableLazyLoad={false}
                                altText={getImageAltText(item.sku)}
                            />
                        </Box>
                    </Box>
                    <Box
                        fontSize='sm'
                        height={'100%'}
                        paddingRight={[4, 4, 4, 0]}
                    >
                        <Flex
                            flexDirection={'column'}
                            justifyContent={'space-between'}
                            height={'100%'}
                        >
                            <Box>
                                <TitleLink
                                    brandName={item.sku.brandName}
                                    productName={item.sku.productName}
                                    targetUrl={targetUrl}
                                    goToTargetUrl={goToTargetUrl}
                                />
                                {abovePriceContent || (
                                    <Box
                                        data-at={Sephora.debug.dataAt('sku_size')}
                                        color={colors.gray}
                                    >
                                        {getText('item', [item.sku.skuId])}
                                    </Box>
                                )}
                                <Price
                                    item={item}
                                    price={price}
                                    addItemSavingsBasketExperience={addItemSavingsBasketExperience}
                                />
                                {belowPriceContent.map((Component, index) => {
                                    return (
                                        <Component
                                            key={`${item.sku.skuId}-${index}`}
                                            item={item}
                                            {...props}
                                        />
                                    );
                                })}
                            </Box>
                            {renderButtonRow({
                                greaterThan: 'sm'
                            })}
                        </Flex>
                    </Box>
                </Grid>
                {renderButtonRow({
                    lessThan: 'md'
                })}
                <ItemSubstitutionRoot item={item} />
            </Flex>

            <Media greaterThan={'xs'}>
                <Box
                    borderLeft={`1px solid ${colors.lightGray}`}
                    paddingLeft={4}
                    minHeight={'100%'}
                    width={'300px'}
                >
                    {rightLGUISection}
                </Box>
            </Media>
        </Grid>
    );
}

export default wrapFunctionalComponent(SkuItemLayout, 'SkuItemLayout');
